import { AnimatePresence, motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useAppDispatch, useAppSelector } from "hooks/hooks";
import { useEffect, useState } from "react";
import { FaPlay, FaTimes } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { MapPin, Calendar, Users, Star, CheckCircle, ArrowRight } from "lucide-react";
import { getHomeDemoVideos } from "redux/features/Admin/homeVideo/homeVideoSlice";
import { getAllLocations } from "redux/features/location/locationSlice";
import img from "../../src/images/bg.png";
import { EASING } from "../utils/easingUtils";

const VideoModal = ({ isOpen, onClose, videoUrl }: { isOpen: boolean; onClose: () => void; videoUrl: string }) => {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.7, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.7, opacity: 0, y: 50 }}
            transition={{ duration: 0.4, ease: EASING.easeOut }}
            className="relative w-full max-w-4xl p-1 mx-4 bg-white shadow-2xl cursor-pointer rounded-2xl"
            onClick={onClose}
          >
            <motion.button
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
              className="absolute z-50 flex items-center justify-center w-10 h-10 text-white transition-colors bg-red-800 rounded-full top-4 right-4 hover:bg-gray-900"
              onClick={onClose}
            >
              <FaTimes className="text-lg" />
            </motion.button>
            <div className="relative w-full h-0 pb-[56.25%] overflow-hidden rounded-xl shadow-inner">
              <video
                className="absolute top-0 left-0 w-full h-full"
                src={process.env.REACT_APP_CLOUD_FRONT + videoUrl}
                autoPlay
                loop
                muted
                controls
              ></video>
            </div>
            {!videoUrl && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="absolute inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 rounded-xl"
              >
                <div className="text-lg font-semibold text-white">Loading video...</div>
              </motion.div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const DemoHomePage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { allLocations } = useAppSelector((state) => state.location);
  const [videoUrl, setVideoUrl] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const fetch = async () => {
    const res = await dispatch(getHomeDemoVideos());
    if (res.payload && res.payload.url) {
      setVideoUrl(res.payload.url);
    }
  };

  // Calculate real statistics from database
  const calculateRealStats = () => {
    if (!Array.isArray(allLocations) || allLocations.length === 0) {
      return {
        locations: 0,
        rating: 4.5,
      };
    }

    const locationsWithRatings = allLocations.filter(
      (location: any) => location.likesAverage && parseFloat(location.likesAverage) > 0
    );

    const avgRating =
      locationsWithRatings.length > 0
        ? locationsWithRatings.reduce((sum: number, location: any) => sum + parseFloat(location.likesAverage), 0) /
          locationsWithRatings.length
        : 4.5;

    return {
      locations: allLocations.length,
      rating: Math.round(avgRating * 10) / 10,
    };
  };

  const realStats = calculateRealStats();

  useEffect(() => {
    fetch();
    dispatch(getAllLocations());
  }, [dispatch]);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const features = [
    { icon: MapPin, text: "Lieux uniques en France" },
    { icon: Calendar, text: "Réservation instantanée" },
    { icon: Users, text: "Support personnalisé" },
    { icon: Star, text: "Espaces vérifiés" },
  ];

  const benefits = [
    `Plus de ${realStats.locations} espaces disponibles`,
    "Réponse en moins de 24h",
    "Assurance incluse",
    "Prix transparents",
  ];

  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-purple-50/30 via-blue-50/20 to-cyan-50/30 dark:from-purple-900/20 dark:via-blue-900/10 dark:to-cyan-900/20">
      {/* Background Effects */}
      <div className="absolute inset-0">
        {[...Array(12)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-purple-300/20 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -30, 0],
              opacity: [0.3, 0.8, 0.3],
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: 6,
              repeat: Infinity,
              delay: Math.random() * 6,
            }}
          />
        ))}
      </div>

      <div className="relative z-10 py-8 lg:py-12" ref={ref}>
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            {/* Left Content */}
            <motion.div
              className="flex-1 text-center lg:text-left"
              initial={{ opacity: 0, x: -50 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
            >
              {/* Title */}
              <motion.h1
                className="text-4xl lg:text-6xl font-bold leading-tight mb-6"
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.3, duration: 0.8 }}
              >
                <span className="text-gray-900 dark:text-white">Louer un espace</span>
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 via-blue-600 to-cyan-600">
                  événementiel
                </span>
                <br />
                <span className="text-gray-900 dark:text-white">et plus encore...</span>
              </motion.h1>

              {/* Description */}
              <motion.p
                className="text-xl text-gray-600 dark:text-gray-400 mb-8 leading-relaxed max-w-2xl mx-auto lg:mx-0"
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.4, duration: 0.6 }}
              >
                Organisez votre événement en toute simplicité grâce à EventToBee. Découvrez notre sélection
                d'espaces événementiels : lieux atypiques, salles de réunion, jardins, villas, péniches...
              </motion.p>

              {/* Features Grid */}
              <motion.div
                className="grid grid-cols-2 gap-4 mb-8"
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.5, duration: 0.6 }}
              >
                {features.map((feature, index) => {
                  const Icon = feature.icon;
                  return (
                    <motion.div
                      key={index}
                      className="flex items-center space-x-3 p-3 bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm rounded-xl border border-white/20"
                      whileHover={{ scale: 1.02, y: -2 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg flex items-center justify-center">
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <span className="text-gray-800 dark:text-gray-200 font-medium text-sm">{feature.text}</span>
                    </motion.div>
                  );
                })}
              </motion.div>

              {/* Benefits List */}
              <motion.div
                className="mb-8"
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.6, duration: 0.6 }}
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {benefits.map((benefit, index) => (
                    <motion.div
                      key={index}
                      className="flex items-center space-x-2"
                      initial={{ opacity: 0, x: -20 }}
                      animate={inView ? { opacity: 1, x: 0 } : {}}
                      transition={{ delay: 0.7 + index * 0.1, duration: 0.5 }}
                    >
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">{benefit}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* CTA Buttons */}
              <motion.div
                className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.8, duration: 0.6 }}
              >
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="group relative overflow-hidden bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-4 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                  onClick={() => navigate("/espace-evenementiel")}
                >
                  <span className="relative z-10 flex items-center space-x-2">
                    <span>Explorer les espaces</span>
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-700 to-blue-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="group relative flex items-center justify-center px-8 py-4 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm text-gray-800 dark:text-white border-2 border-gray-200 dark:border-gray-600 rounded-full font-semibold hover:border-purple-500 dark:hover:border-purple-400 transition-all duration-300"
                  onClick={handleOpenModal}
                >
                  <div className="flex items-center justify-center w-10 h-10 mr-3 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-full group-hover:scale-110 transition-transform duration-300">
                    <FaPlay className="text-sm ml-0.5" />
                  </div>
                  <span>Voir la démo</span>
                </motion.button>
              </motion.div>
            </motion.div>

            {/* Right Image */}
            <motion.div
              className="flex-1 relative"
              initial={{ opacity: 0, x: 50 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              <div className="relative">
                {/* Main Image Container */}
                <div className="relative overflow-hidden">
                  <img src={img} alt="Espace événementiel moderne" className="w-full h-[500px] object-cover" />

                  {/* Gradient Overlay */}

                  {/* Floating Stats Cards */}
                  <motion.div
                    className="absolute top-6 left-6 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-2xl p-4 shadow-lg"
                    initial={{ opacity: 0, y: 20 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: 1, duration: 0.6 }}
                  >
                    <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                      {realStats.locations.toLocaleString()}+
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Espaces disponibles</div>
                  </motion.div>

                  <motion.div
                    className="absolute bottom-6 right-6 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-2xl p-4 shadow-lg"
                    initial={{ opacity: 0, y: 20 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: 1.2, duration: 0.6 }}
                  >
                    <div className="flex items-center space-x-2">
                      <Star className="w-5 h-5 text-yellow-400 fill-current" />
                      <span className="text-xl font-bold text-gray-800 dark:text-white">{realStats.rating}</span>
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Note moyenne</div>
                  </motion.div>
                </div>

                {/* Decorative Elements */}
                <motion.div
                  className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-r from-purple-400 to-blue-400 rounded-full opacity-20 blur-xl"
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0.2, 0.3, 0.2],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                  }}
                />
                <motion.div
                  className="absolute -bottom-4 -left-4 w-32 h-32 bg-gradient-to-r from-cyan-400 to-purple-400 rounded-full opacity-20 blur-xl"
                  animate={{
                    scale: [1.2, 1, 1.2],
                    opacity: [0.3, 0.2, 0.3],
                  }}
                  transition={{
                    duration: 5,
                    repeat: Infinity,
                  }}
                />
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      <VideoModal isOpen={isModalOpen} onClose={handleCloseModal} videoUrl={videoUrl} />
    </div>
  );
};

export default DemoHomePage;
